import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { pluginApi, loggingApi, serverApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Loader2, Save, ScrollText, MessageSquare, UserPlus, UserMinus, Ban, Shield, Webhook, RefreshCcw, BarChart3, Search } from 'lucide-react';
import { toast } from 'sonner';

export function LoggingPlugin({ serverId }: { serverId: string }) {
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const { data: channels = [] } = useQuery({
    queryKey: ['servers', serverId, 'channels'],
    queryFn: () => serverApi.getChannels(serverId),
  });

  const loggingPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'logging');
  const isEnabled = loggingPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) => 
      enabled ? pluginApi.enablePlugin(serverId, 'logging') : pluginApi.disablePlugin(serverId, 'logging'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving } = useApiMutation(
    (config: any) => pluginApi.updatePluginConfig(serverId, 'logging', config),
    { 
      onSuccess: () => toast.success('Configuration saved!'),
      invalidateQueries: [['servers', serverId, 'plugins']] 
    }
  );

  const [logTypeFilter, setLogTypeFilter] = useState<string>('all');
  const [logSearch, setLogSearch] = useState<string>('');

  const { data: logEntries = [], isLoading: logsLoading, refetch: refetchLogs } = useQuery({
    queryKey: ['servers', serverId, 'logging', 'entries', logTypeFilter, logSearch],
    queryFn: () =>
      loggingApi.getEntries(
        serverId,
        50,
        logTypeFilter === 'all' ? undefined : logTypeFilter,
        undefined,
        logSearch.trim() || undefined
      ),
    enabled: isEnabled,
  });

  const { data: analysis, isLoading: analysisLoading, refetch: refetchAnalysis } = useQuery({
    queryKey: ['servers', serverId, 'logging', 'analysis'],
    queryFn: () => loggingApi.getAnalysis(serverId, 24),
    enabled: isEnabled,
  });

  const { mutate: createWebhook, isPending: creatingWebhook } = useApiMutation(
    ({ channelId, name }: { channelId: string; name?: string }) =>
      loggingApi.createWebhook(serverId, channelId, name),
    {
      onSuccess: (data: any) => {
        const webhookUrl = data?.url;
        if (webhookUrl) {
          setConfig((prev: any) => ({ ...prev, logWebhookUrl: webhookUrl }));
          updateConfig({ ...config, logWebhookUrl: webhookUrl });
          toast.success('Webhook creato e salvato!');
        }
      },
      onError: () => toast.error('Errore creazione webhook'),
    }
  );

  const [config, setConfig] = useState<any>({
    enabled: true,
    logChannelId: '',
    logWebhookUrl: '',
    logMessageEdits: true,
    logMessageDeletes: true,
    logMemberJoins: true,
    logMemberLeaves: true,
    logBans: true,
    logUnbans: true,
    logRoleChanges: true,
    logChannelChanges: true,
    logServerChanges: true,
    ignoreBotsLogs: true
  });

  const [webhookChannelId, setWebhookChannelId] = useState('');
  const [webhookName, setWebhookName] = useState('Community Hub Logs');

  const knownTypes = useMemo(() => {
    const found = new Set<string>();
    (logEntries as any[]).forEach((entry) => {
      if (entry?.type) found.add(String(entry.type));
    });
    return ['all', ...Array.from(found).sort()];
  }, [logEntries]);

  useEffect(() => {
    if (loggingPlugin?.config) {
      setConfig({ ...config, ...loggingPlugin.config });
    }
  }, [loggingPlugin]);

  const handleSave = () => {
    updateConfig(config);
  };

  if (!loggingPlugin) return null;

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-500/20 rounded-xl">
            <ScrollText className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Logging System</h3>
            <p className="text-sm text-muted-foreground">
              Track all server events and changes.
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={(enabled) => togglePlugin({ enabled })}
        />
      </div>

      {isEnabled && (
        <>
          {/* Log Channel */}
          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <ScrollText className="w-5 h-5 text-blue-400" />
              <h4 className="font-semibold">Log Channel</h4>
            </div>

            <div>
              <Label>Log Channel ID</Label>
              <Select
                value={config.logChannelId || 'none'}
                onValueChange={(value) => setConfig({ ...config, logChannelId: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona canale log" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun canale</SelectItem>
                  {(channels as any[]).map((channel: any) => (
                    <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Seleziona il canale dove inviare i log.
              </p>
            </div>
          </div>

          {/* Webhook Creator */}
          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Webhook className="w-5 h-5 text-blue-400" />
              <h4 className="font-semibold">Webhook Creator</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Channel ID</Label>
                <Select
                  value={webhookChannelId || 'none'}
                  onValueChange={(value) => setWebhookChannelId(value === 'none' ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona canale webhook" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun canale</SelectItem>
                    {(channels as any[]).map((channel: any) => (
                      <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Webhook Name</Label>
                <Input
                  placeholder="Webhook name"
                  value={webhookName}
                  onChange={(e) => setWebhookName(e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-3">
              <Button
                onClick={() => createWebhook({ channelId: webhookChannelId, name: webhookName })}
                disabled={!webhookChannelId || creatingWebhook}
              >
                {creatingWebhook ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Webhook className="w-4 h-4 mr-2" />}
                Crea Webhook
              </Button>
              <div className="flex-1">
                <Label>Webhook URL</Label>
                <Input
                  placeholder="Webhook URL"
                  value={config.logWebhookUrl || ''}
                  onChange={(e) => setConfig({ ...config, logWebhookUrl: e.target.value })}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Se impostato, i log verranno inviati via webhook (in alternativa al canale).
            </p>
          </div>

          {/* Log GUI */}
          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <ScrollText className="w-5 h-5 text-blue-400" />
                <h4 className="font-semibold">Log GUI</h4>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  refetchLogs();
                  refetchAnalysis();
                }}
              >
                <RefreshCcw className="w-4 h-4 mr-2" />
                Aggiorna
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Filtro tipo</Label>
                <Select value={logTypeFilter} onValueChange={setLogTypeFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tutti i tipi" />
                  </SelectTrigger>
                  <SelectContent>
                    {knownTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type === 'all' ? 'Tutti' : type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Ricerca nel registro attività</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    className="pl-9"
                    placeholder="Cerca in action o details (utente, canale, reason...)"
                    value={logSearch}
                    onChange={(e) => setLogSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-4 h-4 text-blue-400" />
                <h5 className="font-medium">Analisi registro attività (ultime 24h)</h5>
              </div>

              {analysisLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analisi in corso...
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <div className="text-xs text-muted-foreground">Eventi totali</div>
                    <div className="text-lg font-semibold">{(analysis as any)?.total ?? 0}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5 border border-white/10 md:col-span-2">
                    <div className="text-xs text-muted-foreground mb-1">Top azioni</div>
                    <div className="flex flex-wrap gap-2">
                      {((analysis as any)?.topActions || []).slice(0, 6).map((row: any) => (
                        <span key={row.action} className="px-2 py-1 rounded-md bg-blue-500/10 border border-blue-500/30 text-xs">
                          {row.action}: {row.count}
                        </span>
                      ))}
                      {((analysis as any)?.topActions || []).length === 0 && (
                        <span className="text-xs text-muted-foreground">Nessun dato</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {logsLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Caricamento log...
              </div>
            ) : (logEntries as any[]).length > 0 ? (
              <div className="space-y-2">
                {(logEntries as any[]).map((entry: any) => (
                  <div key={entry.id} className="p-3 bg-white/5 rounded-lg border border-white/10 space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{entry.summary || entry.action}</span>
                        <span className="text-xs text-muted-foreground">{entry.type} • {entry.action}</span>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : ''}
                      </span>
                    </div>

                    {entry.details && (
                      <details className="text-xs text-muted-foreground">
                        <summary className="cursor-pointer select-none">Dettagli evento</summary>
                        <pre className="mt-2 whitespace-pre-wrap break-words bg-black/20 p-2 rounded-md overflow-x-auto">
                          {JSON.stringify(entry.details, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Nessun log disponibile.</p>
            )}
          </div>

          {/* Message Logs */}
          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-5 h-5 text-blue-400" />
              <h4 className="font-semibold">Message Events</h4>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-sm">Log Message Edits</Label>
                  <p className="text-xs text-muted-foreground">Track when messages are edited</p>
                </div>
                <Switch
                  checked={config.logMessageEdits}
                  onCheckedChange={(checked) => setConfig({ ...config, logMessageEdits: checked })}
                />
              </div>

              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-sm">Log Message Deletes</Label>
                  <p className="text-xs text-muted-foreground">Track when messages are deleted</p>
                </div>
                <Switch
                  checked={config.logMessageDeletes}
                  onCheckedChange={(checked) => setConfig({ ...config, logMessageDeletes: checked })}
                />
              </div>
            </div>
          </div>

          {/* Member Events */}
          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <UserPlus className="w-5 h-5 text-blue-400" />
              <h4 className="font-semibold">Member Events</h4>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-sm">Log Member Joins</Label>
                  <p className="text-xs text-muted-foreground">Track when members join</p>
                </div>
                <Switch
                  checked={config.logMemberJoins}
                  onCheckedChange={(checked) => setConfig({ ...config, logMemberJoins: checked })}
                />
              </div>

              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-sm">Log Member Leaves</Label>
                  <p className="text-xs text-muted-foreground">Track when members leave</p>
                </div>
                <Switch
                  checked={config.logMemberLeaves}
                  onCheckedChange={(checked) => setConfig({ ...config, logMemberLeaves: checked })}
                />
              </div>

              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-sm">Log Role Changes</Label>
                  <p className="text-xs text-muted-foreground">Track role assignments/removals</p>
                </div>
                <Switch
                  checked={config.logRoleChanges}
                  onCheckedChange={(checked) => setConfig({ ...config, logRoleChanges: checked })}
                />
              </div>
            </div>
          </div>

          {/* Moderation Events */}
          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Ban className="w-5 h-5 text-blue-400" />
              <h4 className="font-semibold">Moderation Events</h4>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-sm">Log Bans</Label>
                  <p className="text-xs text-muted-foreground">Track when members are banned</p>
                </div>
                <Switch
                  checked={config.logBans}
                  onCheckedChange={(checked) => setConfig({ ...config, logBans: checked })}
                />
              </div>

              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-sm">Log Unbans</Label>
                  <p className="text-xs text-muted-foreground">Track when members are unbanned</p>
                </div>
                <Switch
                  checked={config.logUnbans}
                  onCheckedChange={(checked) => setConfig({ ...config, logUnbans: checked })}
                />
              </div>
            </div>
          </div>

          {/* Server Events */}
          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-blue-400" />
              <h4 className="font-semibold">Server Events</h4>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-sm">Log Channel Changes</Label>
                  <p className="text-xs text-muted-foreground">Track channel create/delete/update</p>
                </div>
                <Switch
                  checked={config.logChannelChanges}
                  onCheckedChange={(checked) => setConfig({ ...config, logChannelChanges: checked })}
                />
              </div>

              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-sm">Log Server Changes</Label>
                  <p className="text-xs text-muted-foreground">Track server name/icon changes</p>
                </div>
                <Switch
                  checked={config.logServerChanges}
                  onCheckedChange={(checked) => setConfig({ ...config, logServerChanges: checked })}
                />
              </div>

              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-sm">Ignore Bot Logs</Label>
                  <p className="text-xs text-muted-foreground">Don't log events from bots</p>
                </div>
                <Switch
                  checked={config.ignoreBotsLogs}
                  onCheckedChange={(checked) => setConfig({ ...config, ignoreBotsLogs: checked })}
                />
              </div>
            </div>
          </div>

          {/* Commands Info */}
          <div className="glass-card p-6 space-y-4">
            <h4 className="font-semibold mb-2">Available Commands</h4>
            <div className="p-3 bg-white/5 rounded-lg">
              <code className="text-blue-400">/logs</code>
              <p className="text-muted-foreground mt-1">View recent log entries</p>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Configuration
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
